import { ArrowDown, ArrowUp, CheckCheck, Hash } from '@/components/ui/icons';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { MessageTargetRef } from '@clanfire/shared';
import { Skeleton, Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/Feedback';
import { useIsDesktop } from '@/hooks/useAppEffects';
import { useChannelPermissions } from '@/lib/permissions';
import { formatClock, formatDay, formatMessageTime, GROUP_WINDOW_MS, isSameDay } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { targetKey, useMessages, useTargetMessages, type ClientMessage } from '@/stores/messages';
import { useServers } from '@/stores/servers';
import { toast, useUi } from '@/stores/ui';
import { SystemMessage } from './SystemMessage';
import { MessageItem } from './MessageItem';

interface MessageListProps {
  target: MessageTargetRef;
  serverId?: string;
  /** Messages newer than this (from others) get a "New" divider. */
  initialReadAt: string | null;
  welcome: ReactNode;
}

const NEAR_EDGE = 320;

export function MessageList({ target, serverId, initialReadAt, welcome }: MessageListProps) {
  const key = targetKey(target);
  const data = useTargetMessages(key);
  const myId = useAuth((s) => s.user?.id ?? '');
  const display = useAuth((s) => s.settings?.messageDisplay ?? 'COZY');
  const compact = display === 'COMPACT';
  // Bubbles if you picked them; phones show them for Cozy too. (The hook is
  // called first, every render: inside the condition it would be skipped for Bubbles.)
  const isDesktop = useIsDesktop();
  const bubbles = display === 'BUBBLES' || (!isDesktop && display === 'COZY');
  const perms = useChannelPermissions(target.channelId);
  const canManageMessages = serverId ? perms.can('MANAGE_MESSAGES') : false;
  const canReact = serverId ? perms.can('ADD_REACTIONS') : true;
  // Threads start from messages in text channels (not in threads or DMs).
  const inTextChannel = useServers((s) => (target.channelId ? s.channels[target.channelId]?.type === 'TEXT' : false));
  const canStartThreads = inTextChannel && perms.can('SEND_MESSAGES');
  const jumpTo = useUi((s) => (s.jumpTo?.key === key ? s.jumpTo : null));
  const clearJump = useUi((s) => s.clearJump);
  const requestJump = useUi((s) => s.requestJump);

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  /** Where the list was last scrolled to, to tell scrolling up from content growing. */
  const lastScrollTop = useRef(0);
  const initialScrollDone = useRef(false);
  const prependAnchor = useRef<{ height: number; top: number } | null>(null);
  const lastIdRef = useRef<string | undefined>(undefined);
  const jumping = useRef(false);
  const [newBelow, setNewBelow] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);
  // "N new messages" bar: shown while the NEW divider is scrolled out of view above.
  const [barDismissed, setBarDismissed] = useState(false);
  const [dividerAbove, setDividerAbove] = useState(false);

  const unread = useMemo(() => {
    if (!initialReadAt) return null;
    const first = data.messages.findIndex((m) => !m.pending && m.author?.id !== myId && m.createdAt > initialReadAt);
    if (first < 0) return null;
    const count = data.messages.slice(first).filter((m) => m.author?.id !== myId && !m.pending).length;
    // If the first loaded message is already unread, there may be more we haven't loaded.
    return { count, more: first === 0 && data.hasMoreBefore, since: data.messages[first]!.createdAt };
  }, [data.messages, data.hasMoreBefore, initialReadAt, myId]);

  const checkDivider = useCallback(() => {
    const el = scrollRef.current;
    const divider = el?.querySelector('[data-new-divider]');
    if (!el || !divider) return setDividerAbove(false);
    const above = divider.getBoundingClientRect().bottom < el.getBoundingClientRect().top;
    setDividerAbove(above);
    // Once the divider has been scrolled into view, the bar has done its job.
    if (!above) setBarDismissed(true);
  }, []);

  useEffect(() => {
    const dismiss = () => setBarDismissed(true);
    window.addEventListener('clanfire:mark-read', dismiss);
    return () => window.removeEventListener('clanfire:mark-read', dismiss);
  }, []);

  const loadBefore = useCallback(() => {
    const el = scrollRef.current;
    const t = useMessages.getState().byTarget[key];
    if (!el || !t || !t.hasMoreBefore || t.loadingBefore) return;
    prependAnchor.current = { height: el.scrollHeight, top: el.scrollTop };
    void useMessages.getState().loadBefore(target);
  }, [key]);

  /* Keep scroll position sensible whenever the list changes. */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || data.status !== 'loaded') return;
    const lastId = data.messages.at(-1)?.id;

    if (prependAnchor.current) {
      // Older messages were inserted above: keep the same message under the viewport.
      el.scrollTop = prependAnchor.current.top + (el.scrollHeight - prependAnchor.current.height);
      prependAnchor.current = null;
    } else if (!initialScrollDone.current) {
      if (!jumping.current) el.scrollTop = el.scrollHeight;
      initialScrollDone.current = true;
    } else if (lastId !== lastIdRef.current) {
      const last = data.messages.at(-1);
      const mine = last?.author?.id === myId && last.pending;
      if (atBottom.current || mine) el.scrollTop = el.scrollHeight;
      else setNewBelow(true);
    }
    lastIdRef.current = lastId;

    // Short history that doesn't fill the screen: fetch more straight away.
    if (el.scrollHeight <= el.clientHeight + 40 && data.hasMoreBefore && !data.loadingBefore) loadBefore();
    if (!barDismissed) checkDivider();
  }, [data.messages, data.status, data.hasMoreBefore, data.loadingBefore, myId, loadBefore]);

  /* Stay pinned to the bottom while content resizes (images, embeds, edits). */
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const observer = new ResizeObserver(() => {
      if (atBottom.current && !jumping.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  /* Jump-to-message requests (reply previews, search results, notifications). */
  useEffect(() => {
    if (!jumpTo) return;
    const id = jumpTo.messageId;
    if (data.messages.some((m) => m.id === id)) {
      jumping.current = false;
      clearJump();
      requestAnimationFrame(() => {
        document.getElementById(`message-${id}`)?.scrollIntoView({ block: 'center' });
        atBottom.current = false;
        setHighlight(id);
        window.setTimeout(() => setHighlight((h) => (h === id ? null : h)), 2000);
      });
      return;
    }
    if (data.status === 'error' && jumping.current) {
      jumping.current = false;
      clearJump();
      toast.error("That message couldn't be found. It may have been deleted.");
      void useMessages.getState().loadLatest(target);
      return;
    }
    if (!jumping.current && data.status !== 'loading') {
      jumping.current = true;
      void useMessages.getState().loadAround(target, id);
    }
  }, [jumpTo, data.messages, data.status]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const previousTop = lastScrollTop.current;
    lastScrollTop.current = el.scrollTop;
    // Pinned to the bottom and not scrolled up: content grew below (an image or
    // preview finished loading, and the browser shifted the view). Stay pinned:
    // only scrolling up lets go.
    if (atBottom.current && el.scrollTop >= previousTop && !data.hasMoreAfter && !jumping.current) {
      el.scrollTop = el.scrollHeight;
      lastScrollTop.current = el.scrollTop;
      return;
    }
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    atBottom.current = distance < 80 && !data.hasMoreAfter;
    if (atBottom.current) setNewBelow(false);
    if (!barDismissed) checkDivider();
    if (el.scrollTop < NEAR_EDGE) loadBefore();
    if (distance < NEAR_EDGE && data.hasMoreAfter && !data.loadingAfter) void useMessages.getState().loadAfter(target);
  };

  const jumpToPresent = () => {
    setNewBelow(false);
    if (data.hasMoreAfter) {
      initialScrollDone.current = false;
      void useMessages.getState().loadLatest(target);
    } else {
      const el = scrollRef.current;
      el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  };

  const jumpToMessage = useCallback((id: string) => requestJump(key, id), [key, requestJump]);

  const jumpToUnread = () => {
    const el = scrollRef.current;
    const divider = el?.querySelector('[data-new-divider]');
    if (!el || !divider) return;
    atBottom.current = false;
    divider.scrollIntoView({ block: 'center', behavior: 'smooth' });
    // Load older pages if the unread run started before what we have.
    if (unread?.more) loadBefore();
  };

  if (data.status === 'error' && data.messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <ErrorState message={data.error ?? 'Could not load messages.'} onRetry={() => void useMessages.getState().loadLatest(target)} />
      </div>
    );
  }

  const loadingInitial = (data.status === 'loading' || data.status === 'idle') && data.messages.length === 0;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {unread && !barDismissed && dividerAbove && (
        <div className="absolute inset-x-3 top-0 z-10 flex animate-slide-up items-stretch overflow-hidden rounded-b-xl bg-accent text-sm font-semibold text-accent-fg shadow-pop sm:inset-x-4">
          <button type="button" onClick={jumpToUnread} className="flex min-w-0 flex-1 items-center gap-2 px-4 py-1.5 text-left hover:bg-black/10">
            <span className="truncate">
              {unread.count}
              {unread.more ? '+' : ''} new message{unread.count === 1 && !unread.more ? '' : 's'} since {isSameDay(unread.since, new Date().toISOString()) ? formatClock(unread.since) : formatMessageTime(unread.since)}
            </span>
            <span className="ml-auto flex shrink-0 items-center gap-1 text-xs opacity-80">
              Jump <ArrowUp size={14} />
            </span>
          </button>
          <button
            type="button"
            onClick={() => setBarDismissed(true)}
            className="flex shrink-0 items-center gap-1.5 border-l border-black/15 px-3 text-xs hover:bg-black/10"
          >
            Mark as read <CheckCheck size={14} />
          </button>
        </div>
      )}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        data-cf="message-list"
        className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-busy={loadingInitial}
      >
        {/* As wide as the chat, or a narrower column with the "readable" layout. */}
        <div ref={contentRef} className="mx-auto flex min-h-full w-full max-w-[var(--cf-chat-width)] flex-col justify-end pb-4">
          {loadingInitial ? (
            <MessageSkeletons />
          ) : (
            <>
              {data.hasMoreBefore ? (
                <div className="flex h-16 items-center justify-center text-fg-muted">{data.loadingBefore && <Spinner size={18} />}</div>
              ) : (
                welcome
              )}
              {data.messages.map((m, i) => {
                const prev = data.messages[i - 1];
                const newDay = !prev || !isSameDay(prev.createdAt, m.createdAt);
                const unreadDivider =
                  !!initialReadAt && !m.pending && m.author?.id !== myId && m.createdAt > initialReadAt && (!prev || prev.createdAt <= initialReadAt);
                const grouped =
                  !!prev &&
                  !newDay &&
                  !unreadDivider &&
                  prev.author?.id === m.author?.id &&
                  prev.type === 'DEFAULT' &&
                  !prev.failed &&
                  new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW_MS;
                return (
                  <Fragment key={m.nonce ?? m.id}>
                    {newDay && <DayDivider iso={m.createdAt} />}
                    {unreadDivider && <NewDivider />}
                    {m.type !== 'DEFAULT' ? (
                      <SystemMessage message={m} highlighted={highlight === m.id} />
                    ) : (
                    <MessageItem
                      message={m as ClientMessage}
                      grouped={grouped}
                      compact={compact}
                      bubble={bubbles}
                      myId={myId}
                      canManageMessages={canManageMessages}
                      canReact={canReact}
                      canStartThreads={canStartThreads}
                      highlighted={highlight === m.id}
                      onJump={jumpToMessage}
                    />
                    )}
                  </Fragment>
                );
              })}
              {data.hasMoreAfter && <div className="flex h-16 items-center justify-center text-fg-muted">{data.loadingAfter && <Spinner size={18} />}</div>}
            </>
          )}
        </div>
      </div>

      {(newBelow || data.hasMoreAfter) && (
        <button
          type="button"
          onClick={jumpToPresent}
          className="absolute bottom-3 left-1/2 flex -translate-x-1/2 animate-slide-up items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-bold text-accent-fg shadow-pop hover:bg-accent-hover"
        >
          {data.hasMoreAfter ? "You're viewing older messages — jump to present" : 'New messages'} <ArrowDown size={16} />
        </button>
      )}
    </div>
  );
}

function DayDivider({ iso }: { iso: string }) {
  return (
    <div role="separator" className="mx-4 mt-6 mb-1 flex items-center gap-3 text-xs text-fg-muted sm:mx-6">
      <span className="h-0.5 flex-1 rounded-full bg-line" />
      {formatDay(iso)}
      <span className="h-0.5 flex-1 rounded-full bg-line" />
    </div>
  );
}

function NewDivider() {
  return (
    <div role="separator" aria-label="New messages" data-new-divider className="relative mx-4 mt-3 mb-1 h-0.5 rounded-full bg-accent sm:mx-6">
      <span className="absolute -top-2 right-0 rounded-full bg-accent px-2 text-[10px] leading-[18px] font-bold text-accent-fg uppercase">New</span>
    </div>
  );
}

function MessageSkeletons() {
  return (
    <div className="flex flex-col gap-6 px-4 py-4 sm:px-6" aria-label="Loading messages">
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className={cn('h-3.5', i % 3 === 0 ? 'w-3/4' : i % 3 === 1 ? 'w-1/2' : 'w-2/3')} />
            {i % 2 === 0 && <Skeleton className="h-3.5 w-1/3" />}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ChannelWelcome({ name, topic }: { name: string; topic: string | null }) {
  return (
    <div className="px-4 pt-8 pb-4 sm:px-6">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-soft text-accent">
        <Hash size={32} />
      </div>
      <h2 className="mt-3 text-3xl">Welcome to #{name}!</h2>
      <p className="mt-1 text-fg-muted">This is the start of the #{name} channel.{topic ? ` ${topic}` : ''}</p>
    </div>
  );
}
